import { AuthBindings } from '@refinedev/core';
import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect, useRef } from 'react';

import { axiosInstance } from '@/core/services';
import logger from '@/core/services';

export type Auth0User = {
  email: string;
  email_verified: boolean;
  family_name: string;
  given_name: string;
  name: string;
  nickname: string;
  picture: string;
  sub: string;
  updated_at: string;
};

export const useAppAuthProvider = () => {
  const {
    isLoading,
    isAuthenticated,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    error: auth0Error,
  } = useAuth0<Auth0User>();
  const [token, setToken] = useState<string | null>(null);
  const loadingPromiseRef = useRef<Promise<void> | null>(null);

  // Throw promise for Suspense when loading
  if (isLoading) {
    if (!loadingPromiseRef.current) {
      loadingPromiseRef.current = new Promise<void>((resolve) => {
        setTimeout(resolve, 100);
      });
    }
    throw loadingPromiseRef.current;
  } else {
    loadingPromiseRef.current = null;
  }

  useEffect(() => {
    if (isAuthenticated && user) {
      getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        },
      })
        .then((accessToken) => {
          if (accessToken) {
            setToken(accessToken);
            axiosInstance.defaults.headers.common = {
              Authorization: `Bearer ${accessToken}`,
            };
          }
        })
        .catch((err) => {
          logger.error('Error getting access token:', err);
        });
    }
  }, [isAuthenticated, isLoading, user, auth0Error, getAccessTokenSilently]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !auth0Error) {
      if (
        !window.location.pathname.includes('/callback') &&
        !window.location.search.includes('code=') &&
        !window.location.search.includes('state=')
      ) {
        loginWithRedirect();
      }
    }
  }, [isLoading, isAuthenticated, auth0Error, loginWithRedirect]);

  const authProvider: AuthBindings = {
    login: async () => {
      loginWithRedirect();
      return {
        success: true,
      };
    },
    logout: async (options?: { redirectPath?: string }) => {
      logout({
        logoutParams: {
          returnTo: options?.redirectPath || window.location.origin,
        },
      });
      return {
        success: true,
      };
    },
    onError: async (error: Error | unknown) => {
      logger.error('Auth error:', error);
      return {
        error:
          error instanceof Error
            ? error
            : new Error('An unknown error occurred'),
      };
    },
    check: async () => {
      try {
        if (!isAuthenticated) {
          return {
            authenticated: false,
            error: {
              message: 'Not authenticated',
              name: 'Not authenticated',
            },
            logout: true,
            redirectTo: '/login',
          };
        }

        const accessToken = await getAccessTokenSilently({
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          },
        });

        if (accessToken) {
          setToken(accessToken);
          axiosInstance.defaults.headers.common = {
            Authorization: `Bearer ${accessToken}`,
          };
          return {
            authenticated: true,
          };
        }

        return {
          authenticated: false,
          error: {
            message: 'Token not found',
            name: 'Token not found',
          },
          logout: true,
          redirectTo: '/login',
        };
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unknown error during authentication check';
        logger.error('Auth check failed:', errorMessage, error);
        return {
          authenticated: false,
          error: new Error(errorMessage),
          logout: true,
          redirectTo: '/login',
        };
      }
    },
    getIdentity: async () => {
      return user;
    },
  };

  return {
    authProvider,
    isLoading,
    isAuthenticated,
    user,
    token,
    auth0Error,
  };
};
